import { useCallback, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { ConfirmDialog } from '../../school_panel/components/ui';

/** An exam as the guard needs it: `owner_school` is the real school that owns it (null for the Hub's own). */
export type GuardedExam = { id: number | string; owner_school?: string | null } | null | undefined;

/**
 * A superuser may change any exam, but before the first change to another
 * school's exam (anything not owned by the YADA Education Hub) they are asked
 * once. After "Continue" that exam is not asked about again until the page
 * is left. Everyone else, and Hub exams, run the action straight away.
 */
export function useOtherSchoolGuard() {
  const { role } = useAuth();
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<{ key: string; school: string; run: () => void } | null>(null);

  const needsConfirm = useCallback(
    (exam: GuardedExam) => role === 'superuser' && !!exam?.owner_school && !confirmed.has(String(exam.id)),
    [role, confirmed],
  );

  const guard = useCallback((exam: GuardedExam, run: () => void) => {
    if (!needsConfirm(exam)) { run(); return; }
    setPending({ key: String(exam!.id), school: exam!.owner_school!, run });
  }, [needsConfirm]);

  const dialog = (
    <ConfirmDialog
      isOpen={!!pending}
      title="This is another school's exam"
      body={pending ? `You're trying to change an exam that belongs to ${pending.school}. Your changes reach that school's staff and students.` : null}
      confirmLabel="Continue"
      danger={false}
      onConfirm={() => {
        if (!pending) return;
        const { key, run } = pending;
        setConfirmed((prev) => new Set(prev).add(key));
        setPending(null);
        run();
      }}
      onCancel={() => setPending(null)}
    />
  );

  return { guard, needsConfirm, dialog };
}
